import { message } from 'antd'

// Use any for flexibility with the existing interfaces
type ReservationInfo = any
type ChargeData = any

// Convert time to user-friendly format
const convertTime = (timeString: string | Date) => {
  const date = new Date(timeString)
  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }
  return date.toLocaleDateString('en-GB', options)
}

// Function to create canvas and draw receipt
const createReceiptCanvas = (
  reservationInfo: ReservationInfo,
  charge: ChargeData,
): HTMLCanvasElement => {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!

  // Set canvas dimensions
  canvas.width = 600
  canvas.height = 800

  // Set white background
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  let yPosition = 40

  // Header
  ctx.fillStyle = '#0048B3'
  ctx.fillRect(0, 0, canvas.width, 80)

  // Company name
  ctx.fillStyle = '#ffffff'
  ctx.font = 'bold 24px Arial'
  ctx.textAlign = 'center'
  ctx.fillText('HAUP CAR', canvas.width / 2, 30)

  // Receipt title
  ctx.font = 'bold 18px Arial'
  ctx.fillText('Payment Receipt', canvas.width / 2, 55)

  yPosition = 100

  // Success badge
  ctx.fillStyle = '#52c41a'
  ctx.fillRect(200, yPosition, 200, 30)
  ctx.fillStyle = '#ffffff'
  ctx.font = 'bold 14px Arial'
  ctx.fillText('✓ Payment Successful', canvas.width / 2, yPosition + 20)

  yPosition += 60

  // Helper function to draw detail row
  const drawDetailRow = (label: string, value: string, y: number) => {
    ctx.fillStyle = '#666666'
    ctx.font = 'bold 14px Arial'
    ctx.textAlign = 'left'
    ctx.fillText(label, 40, y)

    ctx.fillStyle = '#333333'
    ctx.font = '14px Arial'
    ctx.textAlign = 'right'
    ctx.fillText(value, canvas.width - 40, y)

    // Draw line
    ctx.strokeStyle = '#f0f0f0'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(40, y + 5)
    ctx.lineTo(canvas.width - 40, y + 5)
    ctx.stroke()
  }

  // Section header
  ctx.fillStyle = '#333333'
  ctx.font = 'bold 16px Arial'
  ctx.textAlign = 'left'
  ctx.fillText('Transaction Details', 40, yPosition)
  yPosition += 30

  // Transaction details
  const allChargesForId: any[] = reservationInfo?.reservationData?.chargeData || []
  const depositChargeForId = allChargesForId.find(
    (c) =>
      c.description?.includes('PRE-CHECKIN SYSTEM') &&
      c.chargestate?.includes('CHARGED') &&
      c.charge_deposit > 0,
  )
  drawDetailRow(
    'Transaction ID:',
    depositChargeForId?.chargeid || charge?.chargeid || 'N/A',
    yPosition,
  )
  yPosition += 25

  drawDetailRow('Payment Method:', charge?.chargemethod || 'N/A', yPosition)
  yPosition += 25

  drawDetailRow('Status:', charge?.chargestate || 'CHARGED', yPosition)
  yPosition += 25

  drawDetailRow(
    'Payment Date:',
    charge?.logtime
      ? new Date(charge.logtime).toLocaleDateString('en-GB')
      : new Date().toLocaleDateString('en-GB'),
    yPosition,
  )
  yPosition += 40

  // Reservation section
  ctx.fillStyle = '#333333'
  ctx.font = 'bold 16px Arial'
  ctx.textAlign = 'left'
  ctx.fillText('Reservation Information', 40, yPosition)
  yPosition += 30

  drawDetailRow(
    'Reservation No:',
    reservationInfo?.reservationData?.reservationno || 'N/A',
    yPosition,
  )
  yPosition += 25

  const customerName =
    reservationInfo?.userData?.name || reservationInfo?.userData?.lastname
      ? `${reservationInfo?.userData?.name || ''} ${
          reservationInfo?.userData?.lastname || ''
        }`.trim()
      : reservationInfo?.userData?.email || 'N/A'
  drawDetailRow('Customer Name:', customerName, yPosition)
  yPosition += 25

  const vehicle = reservationInfo?.reservationData?.vehicleData
    ? `${reservationInfo.reservationData.vehicleData.vehiclebrand || ''} ${
        reservationInfo.reservationData.vehicleData.vehiclemodel || ''
      }`.trim()
    : 'N/A'
  drawDetailRow('Vehicle:', vehicle, yPosition)
  yPosition += 25

  drawDetailRow('Pick-up Location:', reservationInfo?.stationDetail?.name || 'N/A', yPosition)
  yPosition += 25

  const startTime = reservationInfo?.reservationData?.initstarttime
    ? convertTime(reservationInfo.reservationData.initstarttime)
    : 'N/A'
  drawDetailRow('Start Time:', startTime, yPosition)
  yPosition += 25

  const endTime = reservationInfo?.reservationData?.initstoptime
    ? convertTime(reservationInfo.reservationData.initstoptime)
    : 'N/A'
  drawDetailRow('End Time:', endTime, yPosition)
  yPosition += 40

  // Resolve PRE-CHECKIN charge breakdown
  const allCharges: any[] = reservationInfo?.reservationData?.chargeData || []
  const preCheckinCharged = allCharges.filter(
    (c) => c.description?.includes('PRE-CHECKIN SYSTEM') && c.chargestate?.includes('CHARGED'),
  )
  const depositCharge = preCheckinCharged.find((c: any) => c.charge_deposit > 0)
  const feeCharge = preCheckinCharged.find((c: any) => c.charge_paymentfee > 0)
  const depositAmt: number =
    depositCharge?.charge_deposit ||
    (reservationInfo?.reservationData?.overide_deposit !== -1
      ? reservationInfo?.reservationData?.overide_deposit
      : reservationInfo?.reservationData?.depositprice_b) ||
    0
  const feeAmt: number = feeCharge?.charge_paymentfee || 0
  const totalAmt = depositAmt + feeAmt

  // Amount section with background
  const boxHeight = feeAmt > 0 ? 110 : 60
  ctx.fillStyle = '#f8f9fa'
  ctx.fillRect(40, yPosition, canvas.width - 80, boxHeight)

  if (feeAmt > 0) {
    ctx.fillStyle = '#666666'
    ctx.font = '14px Arial'
    ctx.textAlign = 'left'
    ctx.fillText('Deposit:', 60, yPosition + 22)
    ctx.fillStyle = '#333333'
    ctx.textAlign = 'right'
    ctx.fillText(`${depositAmt.toLocaleString()} THB`, canvas.width - 60, yPosition + 22)

    ctx.fillStyle = '#666666'
    ctx.textAlign = 'left'
    ctx.fillText('Payment fee (3%):', 60, yPosition + 44)
    ctx.fillStyle = '#333333'
    ctx.textAlign = 'right'
    ctx.fillText(`${feeAmt.toLocaleString()} THB`, canvas.width - 60, yPosition + 44)

    ctx.strokeStyle = '#dddddd'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(60, yPosition + 55)
    ctx.lineTo(canvas.width - 60, yPosition + 55)
    ctx.stroke()

    ctx.fillStyle = '#666666'
    ctx.font = 'bold 18px Arial'
    ctx.textAlign = 'left'
    ctx.fillText('Total charged:', 60, yPosition + 85)
    ctx.fillStyle = '#0048B3'
    ctx.font = 'bold 24px Arial'
    ctx.textAlign = 'right'
    ctx.fillText(`${totalAmt.toLocaleString()} THB`, canvas.width - 60, yPosition + 85)
  } else {
    ctx.fillStyle = '#666666'
    ctx.font = 'bold 18px Arial'
    ctx.textAlign = 'left'
    ctx.fillText('Deposit Amount:', 60, yPosition + 35)
    ctx.fillStyle = '#0048B3'
    ctx.font = 'bold 24px Arial'
    ctx.textAlign = 'right'
    ctx.fillText(`${depositAmt.toLocaleString()} THB`, canvas.width - 60, yPosition + 35)
  }

  yPosition += boxHeight + 20

  // Footer
  ctx.fillStyle = '#666666'
  ctx.font = '12px Arial'
  ctx.textAlign = 'center'
  ctx.fillText('Thank you for choosing HAUP CAR!', canvas.width / 2, yPosition)
  yPosition += 20
  ctx.fillText(`Generated on: ${new Date().toLocaleString('en-GB')}`, canvas.width / 2, yPosition)
  yPosition += 15
  ctx.fillText(
    'This is an electronic receipt for your deposit payment.',
    canvas.width / 2,
    yPosition,
  )

  return canvas
}

// Function to download receipt as image
export const downloadReceiptAsImage = (reservationInfo: ReservationInfo, charge: ChargeData) => {
  try {
    const canvas = createReceiptCanvas(reservationInfo, charge)

    // Convert canvas to blob
    canvas.toBlob(
      (blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = `HAUP_Receipt_${
            reservationInfo?.reservationData?.reservationno || 'unknown'
          }_${new Date().toISOString().split('T')[0]}.png`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          URL.revokeObjectURL(url)
          message.success('Receipt downloaded successfully!')
        } else {
          message.error('Failed to generate receipt image')
        }
      },
      'image/png',
      0.95,
    )
  } catch (error) {
    console.error('Error generating receipt:', error)
    message.error('Failed to generate receipt')
  }
}

// Function to get the successful charge details
export const getSuccessfulCharge = (reservationInfo: ReservationInfo | null) => {
  if (!reservationInfo?.reservationData) return null

  const chargeData = (reservationInfo.reservationData as any)?.chargeData
  if (chargeData && chargeData.length > 0) {
    return chargeData
      .slice()
      .reverse()
      .find((charge: any) => charge.chargestate && charge.chargestate.includes('CHARGED'))
  }
  return null
}
